import { useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import styles from './ui.module.css';

interface TagInputProps {
  values: string[];
  label: string;
  placeholder: string;
  removeLabel: (value: string) => string;
  onChange: (values: string[]) => void;
}

export function TagInput({ values, label, placeholder, removeLabel, onChange }: TagInputProps) {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const value = draft.trim();
    if (!value) return;
    if (!values.some((item) => item.toLocaleLowerCase() === value.toLocaleLowerCase())) {
      onChange([...values, value]);
    }
    setDraft('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commit();
    } else if (event.key === 'Backspace' && !draft && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div className={styles.tagInput} aria-label={label}>
      {values.map((value) => (
        <span className={styles.tag} key={value}>
          <span className={styles.tagText}>{value}</span>
          <button
            type="button"
            className={styles.tagRemove}
            aria-label={removeLabel(value)}
            title={removeLabel(value)}
            onClick={() => onChange(values.filter((item) => item !== value))}
          >
            <X size={12} strokeWidth={2} aria-hidden="true" />
          </button>
        </span>
      ))}
      <input
        value={draft}
        aria-label={label}
        placeholder={values.length === 0 ? placeholder : ''}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commit}
      />
    </div>
  );
}
